/* Collection view toggle — switches the product grid between the editorial
   off-grid layout and the hairline grid, and remembers the choice.
   The grid is re-rendered by the theme on filter/sort/paginate, so state is
   re-applied whenever the list changes. */
(() => {
  const STORAGE_KEY = 'sq:collection-view';
  const DEFAULT_VIEW = 'grid';
  const VIEWS = {
    editorial: 'Editorial',
    grid: 'Grid',
  };

  const readView = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored in VIEWS ? stored : DEFAULT_VIEW;
    } catch {
      return DEFAULT_VIEW;
    }
  };

  const writeView = (view) => {
    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch {
      /* private browsing — the choice just won't persist */
    }
  };

  /* The toggle belongs to collection pages. Search reuses the same grid markup
     but always renders as the lattice, to match product-page recommendations. */
  const template =
    document.querySelector('main.content-for-layout')?.dataset.template || '';
  const isCollection = template.startsWith('collection');

  let current = isCollection ? readView() : 'grid';

  const applyToGrids = () => {
    document.querySelectorAll('.product-grid').forEach((grid) => {
      grid.setAttribute('data-sq-view', current);
    });
  };

  const buildToggle = () => {
    const wrap = document.createElement('div');
    wrap.className = 'sq-view-toggle';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Product layout');
    wrap.dataset.sqViewToggle = 'true';

    Object.entries(VIEWS).forEach(([view, label]) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sq-view-toggle__btn';
      btn.textContent = label;
      btn.dataset.sqView = view;
      btn.setAttribute('aria-pressed', String(view === current));
      btn.addEventListener('click', () => {
        current = view;
        writeView(view);
        applyToGrids();
        syncButtons();
      });
      wrap.appendChild(btn);
    });

    return wrap;
  };

  const syncButtons = () => {
    document.querySelectorAll('.sq-view-toggle__btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.sqView === current));
    });
    slideIndicator();
  };

  /* Position the sliding pill under the active option. Measured rather than
     assumed, so it stays correct whatever the labels are. Widths read as 0
     until the toggle has been laid out, so retry until it measures real. */
  const slideIndicator = () => {
    document.querySelectorAll('.sq-view-toggle').forEach((wrap) => {
      const active = wrap.querySelector('[aria-pressed="true"]');
      if (!active) return;
      if (!active.offsetWidth) {
        requestAnimationFrame(slideIndicator);
        return;
      }
      wrap.style.setProperty('--sq-toggle-w', `${active.offsetWidth}px`);
      wrap.style.setProperty('--sq-toggle-x', `${active.offsetLeft - 2}px`);
      wrap.dataset.sqMeasured = 'true';
    });
  };

  const mountToggle = () => {
    if (!isCollection) return;
    const sorting = document.querySelector('sorting-filter-component');
    const grid = document.querySelector('.product-grid');
    if (!grid) return;
    if (document.querySelector('[data-sq-view-toggle]')) return;

    const toggle = buildToggle();
    if ('ResizeObserver' in window) {
      new ResizeObserver(slideIndicator).observe(toggle);
    }

    /* On mobile the sort control lives inside a closed drawer, so anchoring to
       it hides the toggle. Only use it when it is actually on screen. */
    const sortingVisible = sorting && sorting.offsetParent !== null;
    if (sortingVisible && sorting.parentElement) {
      sorting.parentElement.insertBefore(toggle, sorting);
    } else {
      toggle.classList.add('sq-view-toggle--standalone');
      grid.parentElement.insertBefore(toggle, grid);
    }
  };

  const sync = () => {
    applyToGrids();
    mountToggle();
    syncButtons();
  };

  sync();
  document.addEventListener('DOMContentLoaded', sync);
  window.addEventListener('resize', slideIndicator, { passive: true });
  // Fonts landing late can shift button widths — re-measure once settled.
  if (document.fonts?.ready) document.fonts.ready.then(slideIndicator);

  // The theme swaps grid markup on filter/sort/paginate — re-apply after each.
  const host = document.querySelector('results-list') || document.body;
  new MutationObserver(() => sync()).observe(host, {
    childList: true,
    subtree: true,
  });
})();
